import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Icon } from "@fluentui/react/lib/Icon";
import Select from "react-select";
import { ChoiceGroup } from "@fluentui/react/lib/ChoiceGroup";
import { IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { TextField } from "@fluentui/react/lib/TextField";
import { Toggle } from "@fluentui/react/lib/Toggle";

import { FolderListing, InstancesListing, InstancesListingPopUp, AgreementVersionAdvanced } from "../components";
import Modal from "../../../../../components/modal/Modal";
import { TEXT } from "../../../../../shared/constants/texts";
import { ICON } from "../../../../../shared/constants/icons";
import { VIEW_FOLDER_AND_DOCUMENTS } from "../../../../../shared/constants/addToICIToggleView";
import { PAGE_SIZE, PAGE } from "../../../../../shared/constants/paginationConstants";
import { logError } from "../../../../../shared/services/errorLogging";
import { INITIAL_FORM_DATA } from "../../../redux/slice";
import { bactInstanceApiCall, instanceApiCall } from "../../../redux/apiCalls";
import { updateEmailAttachmentFormData } from "../../../utilFunctions";

const EmailAttachmentUpload: React.FC = () => {
  const state = useSelector((state: any) => state.addToIciSlice);
  const {
    singleEmailData,
    associationData,
    selectedAssociation,
    defaultAssociationOfAgreement,
    emailAttachmentFormData,
  } = state;
  const [isAssociationToggle, setIsAssociationToggle] = React.useState(false);
  const [commonAssociation, setCommonAssociation] = React.useState(null);

  React.useEffect(() => {
    if (selectedAssociation) {
      setCommonAssociation(selectedAssociation);
    }
    if (defaultAssociationOfAgreement) {
      setCommonAssociation(defaultAssociationOfAgreement);
    }
  }, []);

  const onAssociationToggleChange = (_e, checked) => {
    const value = !!checked;
    if (value) {
      let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
      updatedEmailAttachmentFormData = updatedEmailAttachmentFormData.map((item) => {
        return {
          ...INITIAL_FORM_DATA,
          attachment: item.attachment,
          selectedAssociation: commonAssociation,
        };
      });
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    } else {
      let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
      updatedEmailAttachmentFormData = updatedEmailAttachmentFormData.map((item) => {
        return {
          ...INITIAL_FORM_DATA,
          attachment: item.attachment,
        };
      });
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    }
    setCommonAssociation(null);
    setIsAssociationToggle(!!checked);
  };

  const onChangeCommonLocation = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData = updatedEmailAttachmentFormData.map((item) => {
      return {
        ...INITIAL_FORM_DATA,
        attachment: item.attachment,
        selectedAssociation: data,
      };
    });
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    setCommonAssociation(data);
  };

  const groupedLocation = [
    {
      label: TEXT.ASSOCIATED_DOCUMENTS,
      options: associationData,
    },
  ];

  const formatGroupLabel = (data) => {
    if (data.label) {
      return (
        <div className="format-group-label-single-email-upload">
          <span>{data.label}</span>
        </div>
      );
    }
    return null;
  };

  const attachments = singleEmailData && singleEmailData.attachments ? singleEmailData.attachments : [];

  return (
    <div className="email-attachment-single-email-upload">
      <p className="attachments">{TEXT.ATTACHMENTS}</p>
      <div className="association-flag-single-email-upload">
        <div className="association-flag-single-email-upload-a11y">
          <Toggle
            tabIndex={1}
            label={TEXT.ASSOCIATION_TOGGLE}
            inlineLabel
            defaultChecked={isAssociationToggle}
            onText="."
            offText="."
            onChange={onAssociationToggleChange}
            styles={associationToggleStyles}
          />
        </div>

        {!isAssociationToggle ? (
          <div className="location-container-common-email-attachment">
            <label htmlFor="location-email-attachment-add-to-ici-toggle">{TEXT.ADD_TO_LOCATION}</label>
            <div className="location-container-email-attachment-a11y" tabIndex={1}>
              <Select
                aria-labelledby="location-email-attachment-add-to-ici-toggle"
                inputId="location-email-attachment-add-to-ici-toggle"
                name="location-email-attachment-add-to-ici-toggle"
                placeholder={TEXT.PLACEHOLDER_SELECT_LOCATION}
                options={groupedLocation}
                formatGroupLabel={formatGroupLabel}
                styles={locationDropdownStyles}
                isClearable
                value={commonAssociation}
                onChange={(option) => onChangeCommonLocation(option)}
                noOptionsMessage={() => <p className="empty-message">{TEXT.NO_LOCATIONS_FOUND}</p>}
                components={{
                  IndicatorSeparator: () => null,
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
      <div className="attachments-container">
        {attachments && attachments.length > 0
          ? attachments.map((attachment, index) => {
              return (
                <SingleAttachment
                  key={index}
                  attachment={attachment}
                  index={index}
                  showAssociation={isAssociationToggle}
                />
              );
            })
          : null}
      </div>
    </div>
  );
};

const SingleAttachment = (props) => {
  const { attachment, index, showAssociation } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.addToIciSlice);
  const { emailAttachmentFormData, associationData, selectedViewFolderAndDocuments, selectedAgreement } = state;
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };
  const isFolderView = selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id;

  const getInstances = async (data) => {
    const emailAttachmentData = { ...data[index] };
    const { selectedAssociation } = emailAttachmentData;
    let errorMsg = errorValidationMsg.ERROR_LOADING_ASSOCIATION_INSTANCES;
    let pageSize = PAGE_SIZE.ASSOCIATION_INSTANCES_DATA_SINGLE_EMAIL_UPLOAD;
    if (isFolderView) {
      errorMsg = errorValidationMsg.ERROR_LOADING_FOLDER_INSTANCES;
      pageSize = PAGE_SIZE.FOLDER_INSTANCES_DATA_SINGLE_EMAIL_UPLOAD;
    }
    if (selectedAssociation.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstances(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstances: response,
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstances: response,
            isLoadingFolderInstances: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
      }
      if (error) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstances: [],
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstances: [],
            isLoadingFolderInstances: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-attachment-upload|EmailAttachmentUpload - getInstances",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstances(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstances: response,
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstances: response,
            isLoadingFolderInstances: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
      }
      if (error) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstances: [],
            isLoadingAssociationInstances: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstances: [],
            isLoadingFolderInstances: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-attachment-upload|EmailAttachmentUpload - getInstances",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const getInstancesPopup = async (data, searchTerm = "") => {
    const emailAttachmentData = { ...data[index] };
    const { selectedAssociation } = emailAttachmentData;
    let errorMsg = errorValidationMsg.ERROR_LOADING_ASSOCIATION_INSTANCES;
    let pageSize = PAGE_SIZE.INSTANCES_SHOW_MORE_POPUP;
    if (isFolderView) {
      errorMsg = errorValidationMsg.ERROR_LOADING_FOLDER_INSTANCES;
      pageSize = PAGE_SIZE.INSTANCES_SHOW_MORE_POPUP;
    }
    if (selectedAssociation.EnableBulkProcessing) {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        agreementCT: selectedAgreement.entityName,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
        searchTerm: searchTerm,
      };
      const instanceResponse = await bactInstanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstancesPopup(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstancesPopUp: response,
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstancesPopUp: response,
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
      }
      if (error) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstancesPopUp: [],
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstancesPopUp: [],
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-attachment-upload|EmailAttachmentUpload - getInstancesPopup",
            callerApi:
              "api/Instance/GetFilteredBACTInstances?entityName=&parentEntityName=&parentId=&pageNumber=&pageSize=",
          },
        });
      }
    } else {
      const requestData = {
        agreementId: selectedAgreement.sysId,
        associationCT: selectedAssociation.Name,
        page: PAGE.ASSOCIATION_INSTANCES_DATA,
        pageSize: pageSize,
        errMsg: errorMsg,
        searchTerm: searchTerm,
      };
      const instanceResponse = await instanceApiCall({
        ...apiCallParams,
        requestData: requestData,
        retryFunctionCall: () => getInstancesPopup(data),
      });
      const { response, error } = instanceResponse;
      if (response) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstancesPopUp: response,
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstancesPopUp: response,
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
      }
      if (error) {
        let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
        if (!isFolderView) {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            associationInstancesPopUp: [],
            isLoadingAssociationInstancesPopUp: false,
          };
        } else {
          updatedEmailAttachmentFormData[index] = {
            ...emailAttachmentData,
            folderInstancesPopUp: [],
            isLoadingFolderInstancesPopUp: false,
          };
        }
        updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
        logError({
          dispatch: dispatch,
          err: error,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|email-attachment-upload|EmailAttachmentUpload - getInstancesPopup",
            callerApi: "api/Agreement/FindAgreements?contractType=&page=&pageSize=",
          },
        });
      }
    }
  };

  const onChangeLocation = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...INITIAL_FORM_DATA,
      attachment: updatedEmailAttachmentFormData[index].attachment,
      selectedAssociation: data,
    };
    let transformedData = updatedEmailAttachmentFormData;
    if (data && data.id === TEXT.AGREEMENT_NEW_VERSION) {
      transformedData = updatedEmailAttachmentFormData.map((item, itemIndex) => {
        if (
          itemIndex !== index &&
          item.selectedAssociation &&
          item.selectedAssociation.id === TEXT.AGREEMENT_NEW_VERSION
        ) {
          let changedItem = { ...item };
          changedItem = {
            ...changedItem,
            selectedAssociation: null,
          };
          return changedItem;
        }
        return item;
      });
    }
    updateEmailAttachmentFormData(transformedData);
  };

  const onUploadChangeType = (_e, option) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (option.key === TEXT.NEW_DOCUMENT) {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: option.key,
        newDocumentName:
          updatedEmailAttachmentFormData[index].attachment.name.split(".").slice(0, -1).join(".") ||
          updatedEmailAttachmentFormData[index].attachment.name,
      };
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    }
    if (option.key === TEXT.REPLACE_EXISTING) {
      if (!isFolderView) {
        updatedEmailAttachmentFormData[index] = {
          ...INITIAL_FORM_DATA,
          attachment: updatedEmailAttachmentFormData[index].attachment,
          selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
          selectedUploadType: option.key,
          isLoadingAssociationInstances: true,
        };
      } else {
        updatedEmailAttachmentFormData[index] = {
          ...INITIAL_FORM_DATA,
          attachment: updatedEmailAttachmentFormData[index].attachment,
          selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
          selectedUploadType: option.key,
          isLoadingFolderInstances: true,
        };
      }
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
      getInstances(updatedEmailAttachmentFormData);
    }
    if (option.key === TEXT.SKIP) {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: option.key,
      };
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    }
  };

  const onChangeNewDocument = (_e, value) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...INITIAL_FORM_DATA,
      attachment: updatedEmailAttachmentFormData[index].attachment,
      selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
      selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
      newDocumentName: value,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const onFolderClickedNewDocument = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (
      updatedEmailAttachmentFormData[index].selectedFolderNewDocument &&
      updatedEmailAttachmentFormData[index].selectedFolderNewDocument.id === data.id
    ) {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        newDocumentName: updatedEmailAttachmentFormData[index].newDocumentName,
        selectedFolderNewDocument: null,
      };
    } else {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        newDocumentName: updatedEmailAttachmentFormData[index].newDocumentName,
        selectedFolderNewDocument: data,
      };
    }
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const onFolderClickedReplaceExisting = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (
      updatedEmailAttachmentFormData[index].selectedFolderReplaceExisting &&
      updatedEmailAttachmentFormData[index].selectedFolderReplaceExisting.id === data.id
    ) {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        selectedFolderReplaceExisting: null,
        folderInstances: updatedEmailAttachmentFormData[index].folderInstances,
      };
    } else {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        selectedFolderReplaceExisting: data,
        folderInstances: updatedEmailAttachmentFormData[index].folderInstances,
      };
    }
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const onAssociationInstanceClicked = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (
      updatedEmailAttachmentFormData[index].replaceAssociationInstance &&
      updatedEmailAttachmentFormData[index].replaceAssociationInstance.EntityInstanceId === data.EntityInstanceId
    ) {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        associationInstances: updatedEmailAttachmentFormData[index].associationInstances,
        replaceAssociationInstance: null,
        replaceAssociationInstancePopup: null,
      };
    } else {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        associationInstances: updatedEmailAttachmentFormData[index].associationInstances,
        replaceAssociationInstance: data,
        replaceAssociationInstancePopup: data,
      };
    }
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const onFolderInstanceClicked = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (
      updatedEmailAttachmentFormData[index].replaceFolderInstance &&
      updatedEmailAttachmentFormData[index].replaceFolderInstance.EntityInstanceId === data.EntityInstanceId
    ) {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        selectedFolderReplaceExisting: updatedEmailAttachmentFormData[index].selectedFolderReplaceExisting,
        folderInstances: updatedEmailAttachmentFormData[index].folderInstances,
        replaceFolderInstance: null,
        replaceFolderInstancePopup: null,
      };
    } else {
      updatedEmailAttachmentFormData[index] = {
        ...INITIAL_FORM_DATA,
        attachment: updatedEmailAttachmentFormData[index].attachment,
        selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
        selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
        selectedFolderReplaceExisting: updatedEmailAttachmentFormData[index].selectedFolderReplaceExisting,
        folderInstances: updatedEmailAttachmentFormData[index].folderInstances,
        replaceFolderInstance: data,
        replaceFolderInstancePopup: data,
      };
    }
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const onShowMoreClickedAssociationInstances = () => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...INITIAL_FORM_DATA,
      attachment: updatedEmailAttachmentFormData[index].attachment,
      selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
      selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
      associationInstances: updatedEmailAttachmentFormData[index].associationInstances,
      replaceAssociationInstance: updatedEmailAttachmentFormData[index].replaceAssociationInstance,
      replaceAssociationInstancePopup: updatedEmailAttachmentFormData[index].replaceAssociationInstancePopup,
      showAssociationInstancesPopUp: true,
      isLoadingAssociationInstancesPopUp: true,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    getInstancesPopup(updatedEmailAttachmentFormData);
  };

  const onAssociationInstanceInPopupClicked = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (
      updatedEmailAttachmentFormData[index] &&
      updatedEmailAttachmentFormData[index].replaceAssociationInstancePopup &&
      updatedEmailAttachmentFormData[index].replaceAssociationInstancePopup.EntityInstanceId === data.EntityInstanceId
    ) {
      updatedEmailAttachmentFormData[index] = {
        ...updatedEmailAttachmentFormData[index],
        replaceAssociationInstancePopup: null,
      };
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    } else {
      updatedEmailAttachmentFormData[index] = {
        ...updatedEmailAttachmentFormData[index],
        replaceAssociationInstancePopup: data,
      };
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    }
  };

  const associationInstancePopupDoneClicked = () => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...updatedEmailAttachmentFormData[index],
      replaceAssociationInstance: updatedEmailAttachmentFormData[index].replaceAssociationInstancePopup,
      showAssociationInstancesPopUp: false,
    };
    const instances = updatedEmailAttachmentFormData[index].associationInstances;
    const replaceInstance = updatedEmailAttachmentFormData[index].replaceAssociationInstance;
    const ifInstancePresentInTen = instances.filter(
      (instance) => instance.EntityInstanceId === replaceInstance.EntityInstanceId
    );
    if (ifInstancePresentInTen && ifInstancePresentInTen.length === 0) {
      const updatedInstances = [...instances];
      updatedInstances.unshift(replaceInstance);
      updatedEmailAttachmentFormData[index] = {
        ...updatedEmailAttachmentFormData[index],
        associationInstances: updatedInstances,
      };
    }
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const hideAssociationInstancesPopup = () => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...updatedEmailAttachmentFormData[index],
      showAssociationInstancesPopUp: false,
      replaceAssociationInstancePopup: updatedEmailAttachmentFormData[index].replaceAssociationInstance,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const fetchAssociationInstancesOnSearch = (searchText = "") => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...updatedEmailAttachmentFormData[index],
      isLoadingAssociationInstancesPopUp: true,
      replaceAssociationInstance: null,
      replaceAssociationInstancePopup: null,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    getInstancesPopup(updatedEmailAttachmentFormData, searchText);
  };

  const onShowMoreClickedFolderInstances = () => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...INITIAL_FORM_DATA,
      attachment: updatedEmailAttachmentFormData[index].attachment,
      selectedAssociation: updatedEmailAttachmentFormData[index].selectedAssociation,
      selectedUploadType: updatedEmailAttachmentFormData[index].selectedUploadType,
      selectedFolderReplaceExisting: updatedEmailAttachmentFormData[index].selectedFolderReplaceExisting,
      folderInstances: updatedEmailAttachmentFormData[index].folderInstances,
      replaceFolderInstance: updatedEmailAttachmentFormData[index].replaceFolderInstance,
      replaceFolderInstancePopup: updatedEmailAttachmentFormData[index].replaceFolderInstancePopup,
      showFolderInstancesPopUp: true,
      isLoadingFolderInstancesPopUp: true,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    getInstancesPopup(updatedEmailAttachmentFormData);
  };

  const onFolderInstanceInPopupClicked = (data) => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    if (
      updatedEmailAttachmentFormData[index] &&
      updatedEmailAttachmentFormData[index].replaceFolderInstancePopup &&
      updatedEmailAttachmentFormData[index].replaceFolderInstancePopup.EntityInstanceId === data.EntityInstanceId
    ) {
      updatedEmailAttachmentFormData[index] = {
        ...updatedEmailAttachmentFormData[index],
        replaceFolderInstancePopup: null,
      };
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    } else {
      updatedEmailAttachmentFormData[index] = {
        ...updatedEmailAttachmentFormData[index],
        replaceFolderInstancePopup: data,
      };
      updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    }
  };

  const folderInstancePopupDoneClicked = () => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...updatedEmailAttachmentFormData[index],
      replaceFolderInstance: updatedEmailAttachmentFormData[index].replaceFolderInstancePopup,
      showFolderInstancesPopUp: false,
    };
    const instances = updatedEmailAttachmentFormData[index].folderInstances;
    const replaceInstance = updatedEmailAttachmentFormData[index].replaceFolderInstance;
    const ifInstancePresentInTen = instances.filter(
      (instance) => instance.EntityInstanceId === replaceInstance.EntityInstanceId
    );
    if (ifInstancePresentInTen && ifInstancePresentInTen.length === 0) {
      const updatedInstances = [...instances];
      updatedInstances.unshift(replaceInstance);
      updatedEmailAttachmentFormData[index] = {
        ...updatedEmailAttachmentFormData[index],
        folderInstances: updatedInstances,
      };
    }
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const hideFolderInstancesPopup = () => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...updatedEmailAttachmentFormData[index],
      showFolderInstancesPopUp: false,
      replaceFolderInstancePopup: updatedEmailAttachmentFormData[index].replaceFolderInstance,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
  };

  const fetchFolderInstancesOnSearch = (searchText = "") => {
    let updatedEmailAttachmentFormData = [...emailAttachmentFormData];
    updatedEmailAttachmentFormData[index] = {
      ...updatedEmailAttachmentFormData[index],
      isLoadingFolderInstancesPopUp: true,
      replaceFolderInstance: null,
      replaceFolderInstancePopup: null,
    };
    updateEmailAttachmentFormData(updatedEmailAttachmentFormData);
    getInstancesPopup(updatedEmailAttachmentFormData, searchText);
  };

  const AGREEMENT_NEW_VERSION_FILE_EXTENSION = ["docx"];
  const UPLOADABLE_FILE_EXTENSIONS = [
    "pdf",
    "docx",
    "doc",
    "xls",
    "xlsx",
    "eml",
    "jpg",
    "jpeg",
    "png",
    "msg",
    "zip",
    "tiff",
    "ppt",
    "pptx",
    "xml",
    "txt",
    "gif",
    "cad",
    "docm",
    "dotx",
    "dotm",
    "dot",
    "xps",
    "mht",
    "mhtml",
    "htm",
    "rtf",
    "odt",
    "html",
    "oft",
    "csv",
    "xlsm",
    "hwp",
  ];
  const fileTypeArr = attachment.name.split(".");
  const fileExtension = fileTypeArr[fileTypeArr.length - 1].toLowerCase() || "";
  let attachmentIcon = ICON.ATTACH;
  let isExchangeItem = false;
  if (fileExtension === "doc" || fileExtension === "docx") {
    attachmentIcon = ICON.DOCX;
  }
  if (fileExtension === "pdf") {
    attachmentIcon = ICON.PDF;
  }
  if (fileExtension === "png" || fileExtension === "jpeg" || fileExtension === "jpg") {
    attachmentIcon = ICON.PIC;
  }
  if (fileExtension === "xls" || fileExtension === "xlsx" || fileExtension === "xlsm" || fileExtension === "csv") {
    attachmentIcon = ICON.EXCEL;
  }

  if (attachment.attachmentType === Office.MailboxEnums.AttachmentType.Item) {
    isExchangeItem = true;
  }

  const formatGroupLabel = (data) => {
    if (data.label) {
      return (
        <div className="format-group-label-single-email-upload">
          <span>{data.label}</span>
        </div>
      );
    }
    return null;
  };

  let groupedLocation = [
    {
      label: TEXT.ASSOCIATED_DOCUMENTS,
      options: associationData,
    },
  ];

  if (AGREEMENT_NEW_VERSION_FILE_EXTENSION.includes(fileExtension)) {
    groupedLocation.unshift({
      label: "",
      options: [
        { id: TEXT.AGREEMENT_NEW_VERSION, label: TEXT.AGREEMENT_NEW_VERSION, value: TEXT.AGREEMENT_NEW_VERSION },
      ],
    });
  }

  const uploadOptions: IChoiceGroupOption[] = [
    {
      key: TEXT.NEW_DOCUMENT,
      text: TEXT.NEW_DOCUMENT,
      disabled: emailAttachmentFormData[index] && emailAttachmentFormData[index].selectedAssociation ? false : true,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.NEW_DOCUMENT} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
    {
      key: TEXT.REPLACE_EXISTING,
      text: TEXT.REPLACE_EXISTING,
      disabled: emailAttachmentFormData[index] && emailAttachmentFormData[index].selectedAssociation ? false : true,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.REPLACE_EXISTING} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
    {
      key: TEXT.SKIP,
      text: TEXT.SKIP,
      disabled: emailAttachmentFormData[index] && emailAttachmentFormData[index].selectedAssociation ? false : true,
      onRenderLabel: (p) => (
        <span id={p.id + TEXT.SKIP} className="ms-ChoiceFieldLabel" style={uploadOptionsRenderLabelStyle}>
          {p.text}
        </span>
      ),
      styles: uploadOptionsStyle,
    },
  ];

  return (
    <div className="single-attachment-single-email-upload">
      <div className="attachment-name">
        <Icon iconName={attachmentIcon} />
        <span className="sr-only">{attachmentIcon}</span>
        <p title={attachment.name}>{attachment.name}</p>
      </div>
      <React.Fragment>
        {UPLOADABLE_FILE_EXTENSIONS.includes(fileExtension) || isExchangeItem ? (
          <div className="email-attachment-upload">
            {showAssociation ? (
              <div className="location-container-email-attachment">
                <label htmlFor={`location-container-email-attachment-a11y${attachment.name}`}>
                  {TEXT.ADD_TO_LOCATION}
                </label>
                <div className={`location-container-email-attachment-a11y${attachment.name}`} tabIndex={1}>
                  <Select
                    aria-labelledby={`location-container-email-attachment-a11y${attachment.name}`}
                    inputId={`location-container-email-attachment-a11y${attachment.name}`}
                    name={`location-container-email-attachment-a11y${attachment.name}`}
                    placeholder={TEXT.PLACEHOLDER_SELECT_LOCATION}
                    options={groupedLocation}
                    formatGroupLabel={formatGroupLabel}
                    styles={locationDropdownStyles}
                    isClearable
                    value={emailAttachmentFormData[index] ? emailAttachmentFormData[index].selectedAssociation : null}
                    onChange={(option) => onChangeLocation(option)}
                    noOptionsMessage={() => <p className="empty-message">{TEXT.NO_LOCATIONS_FOUND}</p>}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    maxMenuHeight={100}
                  />
                </div>
              </div>
            ) : null}
            {emailAttachmentFormData[index] &&
            (!emailAttachmentFormData[index].selectedAssociation ||
              (emailAttachmentFormData[index].selectedAssociation &&
                emailAttachmentFormData[index].selectedAssociation.id !== TEXT.AGREEMENT_NEW_VERSION)) ? (
              <div className="choice-group-container-email-attachment">
                <ChoiceGroup
                  options={uploadOptions}
                  onChange={onUploadChangeType}
                  selectedKey={emailAttachmentFormData[index].selectedUploadType}
                />
              </div>
            ) : (
              <AgreementVersionAdvanced />
            )}
            {emailAttachmentFormData[index] &&
            emailAttachmentFormData[index].selectedUploadType === TEXT.NEW_DOCUMENT ? (
              <div className="new-document-container-email-attachment">
                {!isFolderView ? (
                  <React.Fragment>
                    <label htmlFor="name-of-document-email-thread">{TEXT.NAME_OF_DOCUMENT}</label>
                    <div className="text-field-container-email-attachment">
                      <TextField
                        value={emailAttachmentFormData[index].newDocumentName}
                        onChange={onChangeNewDocument}
                        aria-labelledby="name-of-document-email-attachment"
                        name="name-of-document-email-attachment"
                        id="name-of-document-email-attachment"
                      />
                    </div>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <label>{TEXT.DOCUMENT_LOCATION}</label>
                    <FolderListing
                      selectedData={emailAttachmentFormData[index].selectedFolderNewDocument}
                      onFolderClick={onFolderClickedNewDocument}
                    />
                  </React.Fragment>
                )}
              </div>
            ) : null}
            {emailAttachmentFormData[index] &&
            emailAttachmentFormData[index].selectedUploadType === TEXT.REPLACE_EXISTING ? (
              <div className="replace-existing-container-email-attachment">
                {!isFolderView ? (
                  <InstancesListing
                    isLoading={emailAttachmentFormData[index].isLoadingAssociationInstances}
                    loadingMsg={TEXT.LOADING_ASSOCIATION_INSTANCES}
                    emptyMsg={TEXT.NO_ASSOCIATION_INSTANCES_FOUND}
                    instances={emailAttachmentFormData[index].associationInstances}
                    onInstanceClicked={onAssociationInstanceClicked}
                    selectedInstance={emailAttachmentFormData[index].replaceAssociationInstance}
                    showMoreClicked={onShowMoreClickedAssociationInstances}
                  />
                ) : (
                  <React.Fragment>
                    <label>{TEXT.DOCUMENT_LOCATION}</label>
                    <FolderListing
                      selectedData={emailAttachmentFormData[index].selectedFolderReplaceExisting}
                      onFolderClick={onFolderClickedReplaceExisting}
                    />
                    {emailAttachmentFormData[index].selectedFolderReplaceExisting ? (
                      <InstancesListing
                        isLoading={emailAttachmentFormData[index].isLoadingFolderInstances}
                        loadingMsg={TEXT.LOADING_FOLDER_INSTANCES}
                        emptyMsg={TEXT.NO_FOLDER_INSTANCES_FOUND}
                        instances={emailAttachmentFormData[index].folderInstances.filter(
                          (instance) =>
                            instance.ICMFolderReference ===
                            emailAttachmentFormData[index].selectedFolderReplaceExisting.id
                        )}
                        onInstanceClicked={onFolderInstanceClicked}
                        selectedInstance={emailAttachmentFormData[index].replaceFolderInstance}
                        showMoreClicked={onShowMoreClickedFolderInstances}
                      />
                    ) : null}
                  </React.Fragment>
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="file-format-not-supported">
            <p>{TEXT.FILE_EXTENSION_NOT_SUPPORTED}</p>
          </div>
        )}
      </React.Fragment>
      {emailAttachmentFormData[index] ? (
        <Modal show={emailAttachmentFormData[index].showAssociationInstancesPopUp}>
          <InstancesListingPopUp
            isLoading={emailAttachmentFormData[index].isLoadingAssociationInstancesPopUp}
            loadingMsg={TEXT.LOADING_ASSOCIATION_INSTANCES}
            emptyMsg={TEXT.NO_ASSOCIATION_INSTANCES_FOUND}
            instances={emailAttachmentFormData[index].associationInstancesPopUp}
            onInstanceClicked={onAssociationInstanceInPopupClicked}
            selectedInstance={emailAttachmentFormData[index].replaceAssociationInstancePopup}
            hidePopup={hideAssociationInstancesPopup}
            onDoneClicked={associationInstancePopupDoneClicked}
            onSearch={fetchAssociationInstancesOnSearch}
          />
        </Modal>
      ) : null}
      {emailAttachmentFormData[index] ? (
        <Modal show={emailAttachmentFormData[index].showFolderInstancesPopUp}>
          <InstancesListingPopUp
            isLoading={emailAttachmentFormData[index].isLoadingFolderInstancesPopUp}
            loadingMsg={TEXT.LOADING_FOLDER_INSTANCES}
            emptyMsg={TEXT.NO_FOLDER_INSTANCES_FOUND}
            instances={emailAttachmentFormData[index].folderInstancesPopUp.filter(
              (instance) =>
                instance.ICMFolderReference === emailAttachmentFormData[index].selectedFolderReplaceExisting.id
            )}
            onInstanceClicked={onFolderInstanceInPopupClicked}
            selectedInstance={emailAttachmentFormData[index].replaceFolderInstancePopup}
            hidePopup={hideFolderInstancesPopup}
            onDoneClicked={folderInstancePopupDoneClicked}
            onSearch={fetchFolderInstancesOnSearch}
          />
        </Modal>
      ) : null}
    </div>
  );
};

export default EmailAttachmentUpload;

const locationDropdownStyles = {
  menu: (styles: any) => ({
    ...styles,
    border: "0.0625rem solid #a6a6a6",
    boxShadow: "none",
    marginTop: "-0.25rem",
  }),
  placeholder: (styles: any) => ({
    ...styles,
    backgroundColor: "#fff",
    color: "#000",
  }),
  control: (styles: any) => ({
    ...styles,
    marginTop: "0.125rem",
    backgroundColor: "#fff",
    border: "0.0625rem solid #a6a6a6",
    boxShadow: "none",
    "&:hover": {
      border: "0.0625rem solid #a6a6a6",
    },
    fontSize: "0.75rem",
    borderRadius: "0",
    height: 32,
    minHeight: 32,
  }),
  valueContainer: (styles) => ({
    ...styles,
    height: 32,
    padding: 4,
    marginTop: "-0.125rem",
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    height: 32,
    marginTop: "-0.0625rem",
  }),
  option: (styles: { [x: string]: any }, { isFocused, isSelected }: any) => {
    return {
      ...styles,
      backgroundColor: isSelected ? "#0078d4" : isFocused ? "#f3f2f1" : "#fff",
      outline: isFocused ? "1px solid #00a2ed" : null,
      cursor: isFocused ? "pointer" : "default",
      ":active": {
        ...styles[":active"],
        backgroundColor: "#0078d4",
      },
      borderRadius: "0.25rem",
      fontSize: "0.75rem",
    };
  },
  group: (styles) => {
    return {
      ...styles,
      backgroundColor: "#ebeae8",
      padding: 0,
      marginTop: "-0.25rem",
    };
  },
};

const uploadOptionsStyle = { root: { display: "inline" } };

const uploadOptionsRenderLabelStyle = {
  fontSize: "0.75rem",
  margin: "0.125rem 0 0.375rem",
  color: "#666",
};

const associationToggleStyles = {
  label: {
    fontSize: "0.8125rem",
    fontWeight: "400",
  },
  text: {
    visibility: "hidden",
  },
};
